import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';

import Dashboard from './Dashboard';
import Applications from './Applications';

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64;

const CertificateCellLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const sections = useMemo(() => [
    {
      id: 'dashboard',
      component: Applications,
      path: '/main/certificate-cell/dashboard',
    },
    {
      id: 'applications',
      component: Applications,
      path: '/main/certificate-cell/applications',
    },
    {
      id: 'issue-certificate',
      component: Dashboard,
      path: '/main/certificate-cell/issue-certificate',
    },
    {
      id: 'certificate-history',
      component: Dashboard,
      path: '/main/certificate-cell/certificate-history',
    },
  ], []);

  const [currentSection, setCurrentSection] = useState('dashboard');

  useEffect(() => {
    const section = sections.find((s) => s.path === location.pathname);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const renderCurrentSection = () => {
    const section = sections.find((s) => s.id === currentSection);
    if (!section) return null;
    const SectionComponent = section.component;
    return <SectionComponent />;
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', height: '100vh', pt: `${APP_HEADER_HEIGHT}px` }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: 'relative',
        }}
      >
        {isMobile && !sidebarOpen && (
          <Box sx={{
            position: 'fixed',
            top: `${APP_HEADER_HEIGHT + 10}px`,
            left: 10,
            zIndex: theme.zIndex.drawer + 1,
          }}>
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: 2,
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: '100%' }}>
          <Paper sx={{ minHeight: 'calc(100vh - 280px)', mb: 3 }}>
            {renderCurrentSection()}
          </Paper>
        </Container>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Action completed successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificateCellLayout;
